/**
 * DE CUÁNTO EN CUÁNTO SE MUEVEN LOS GRAMOS con los botones − y + de un ingrediente.
 *
 * EL FALLO (Francisco, 27-08): «al crear el intra, los gramos se mueven de a 1 y debería ser
 * de a 5». Y no era sólo el intra.
 *
 * Aquí había una tabla propia que decía: verduras 50, bebidas vegetales 50, salsas zero 5, y
 * **todo lo demás 1**. Pero el servidor tiene la suya (`backend/redondeo_salida.paso_en_gramos`)
 * y dice otra cosa: verduras, bebidas vegetales y konjac 50, y **todo lo demás 5**. Medido
 * contra producción, en los típicos del intra:
 *
 *     Ciclodextrina (FullGas)   cat 18.3   el botón movía 1   el servidor redondea a 5
 *     Amilopectina              cat 18.3   el botón movía 1   el servidor redondea a 5
 *     Aquarius                  cat 18.1.1 el botón movía 1   el servidor redondea a 5
 *     Palatinosa · Dextrosa     cat 18.3   el botón movía 1   el servidor redondea a 5
 *
 * O sea que el cliente subía de gramo en gramo y en cuanto la app tocaba esa cantidad -- al
 * cuadrar, al sugerir, al recalcular -- se la dejaba en un múltiplo de 5. En el intra se nota
 * más que en ningún sitio porque son polvos que se dosifican de cinco en cinco, pero pasaba en
 * la carne, en el arroz y en todo lo que no fuera verdura.
 *
 * Es la regla de Jesús del 15-08: «que todo acabe en 0 o en 5». La tenía el servidor y no la
 * tenía el botón.
 *
 * LOS DE UNIDADES, COMO EN CALMA (Francisco, 27-10: «necesito que funcionen igual que en
 * Calma»): de una en una, y de media en media los que admiten medias (`permiteMedia`: el
 * kiwi, el arroz al minuto, la hamburguesa...). El huevo y el flan siguen enteros.
 */

//: Se manejan en cantidades grandes, así que 50 g. El konjac (16.4) va con ellas por lo mismo,
//: no porque sea una salsa. Mismos códigos que `PASO_50` del servidor.
const PASO_50 = ['13', '24', '16.4'];

//: Salsas, polvos, carne, arroz... todo lo demás. Es `PASO_GENERAL` del servidor.
const PASO_GENERAL = 5;

const categoriasDe = (food) => {
    const crudo = food?.categorias || '';
    if (Array.isArray(crudo)) return crudo.map((c) => String(c).trim());
    return String(crudo).split('|').map((c) => c.trim());
};

// En alguno de esos códigos o en una subcategoría suya: «16.4.2» cuenta como «16.4», pero
// «16.1» no cuenta como «16» (mismo criterio que `_en_alguna` del servidor).
const enAlguna = (food, codigos) => categoriasDe(food)
    .some((token) => codigos.some((c) => token === c || token.startsWith(`${c}.`)));

/**
 * @param food        el alimento
 * @param esPorUnidad si se cuenta por unidades (lo sabe la pantalla: el campo se llama de
 *                    tres maneras distintas según por dónde haya entrado)
 * @param pesoUnidad  lo que pesa una unidad, en gramos
 */
export const pasoDeCantidad = (food, esPorUnidad, pesoUnidad) => {
    if (esPorUnidad) {
        if (!pesoUnidad) return PASO_GENERAL;
        return permiteMedia(food) ? pesoUnidad / 2 : pesoUnidad;
    }
    if (enAlguna(food, PASO_50)) return 50;
    return PASO_GENERAL;
};

// Mayúsculas y sin tildes, como compara Calma («Plátano» y «PLATANO» son lo mismo).
const sinTildes = (s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();

/**
 * LA REGLA DE CALMA PARA LAS MEDIAS UNIDADES (27-10), la misma que
 * `backend/calma_suggest.admite_media_unidad`: hamburguesas, bagels, arroz al minuto, fruta
 * fresca por piezas (11.1) y el bizcocho y el brazo de My Fitness Meals. Los demás por
 * unidades van enteros (Jesús, 28-09: el flan a «2,5 ud»).
 *
 * Está también aquí porque los alimentos entran en la pantalla por muchas puertas (menús,
 * «Post sugeridos», favoritas, copiar un día...) y cada una copia sus campos: el
 * `permite_media` del servidor se perdía por el camino y el kiwi recién puesto iba de uno en
 * uno. Las dos copias las ata `mediasDeCalma.casos.json`, que pasan el test de aquí y el del
 * servidor (`tests/test_unidades_como_calma_2710.py`): si una cambia sin la otra, salta.
 */
export const admiteMediaUnidad = (food) => {
    const nombre = sinTildes(food?.nombre);
    const lleva = (x) => nombre.includes(sinTildes(x));
    return lleva('hamburguesa') || lleva('bagel')
        || (lleva('brazo') && lleva('My Fitness Meals'))
        || (lleva('bizcocho') && lleva('My Fitness Meals'))
        || enAlguna(food, ['11.1'])
        || (lleva('arroz') && lleva('minuto'));
};

/** ¿Este alimento por unidades admite media unidad? Lo que diga el servidor, si lo trae. */
export const permiteMedia = (food) => {
    if (food && food.permite_media !== undefined && food.permite_media !== null) {
        return Boolean(food.permite_media);
    }
    return admiteMediaUnidad(food);
};

/** El `step` del campo de cantidad: gramos de 1 en 1; unidades enteras, o medias si admite. */
export const pasoDelCampo = (food, porUnidad) => {
    if (!porUnidad) return '1';
    return permiteMedia(food) ? '0.5' : '1';
};

/** Lo mínimo de un alimento por unidades: media unidad si la admite (Calma), si no una entera. */
export const minimoPorUnidad = (food, peso) => (permiteMedia(food) ? peso / 2 : peso);

/**
 * LO GUARDADO, A LA MEDIA MÁS CERCANA EN TODOS (revisor, 27-10). Calma decide el paso de los
 * botones y el mínimo, no cómo se lee lo que ya hay: 23.721 líneas de dev no caen en unidad
 * entera (pan tostado a 16,5 g con unidad de 11 g), y redondearlas a entera enseñaba «2 ud»
 * contando 16,5 g, y al tocar el campo lo reescribía. Es lo mismo que `cantidad_de` del
 * servidor (`core/dieta_para_ver`).
 */
export const unidadesParaVer = (gramos, peso) => {
    const u = (Number(gramos) || 0) / ((Number(peso) || 0) > 0 ? Number(peso) : 100);
    return Math.round(u * 2) / 2;
};

/** `gramos` pasados a unidades para escribirlas: enteras, o medias si el alimento las admite. */
export const unidadesRedondas = (gramos, peso, food) => {
    const u = (Number(gramos) || 0) / ((Number(peso) || 0) > 0 ? Number(peso) : 100);
    return permiteMedia(food) ? Math.round(u * 2) / 2 : Math.round(u);
};
